import React, { ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initial, last } from 'lodash-es';
import classnames from 'classnames';
import InfoIcon from 'ui/Icons/Info.component.svg';
import CaretUpIcon from 'ui/Icons/tabler/caret-up.component.svg';
import CaretDownIcon from 'ui/Icons/tabler/caret-down.component.svg';
import XIcon from 'ui/Icons/tabler/x.component.svg';
import PencilIcon from 'ui/Icons/tabler/pencil.component.svg';
import TrashIcon from 'ui/Icons/tabler/trash.component.svg';
import CheckIcon from 'ui/Icons/tabler/check.component.svg';
import { SvgIcon } from 'ui/SvgIcon';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { ENetworkRequestStatus } from 'services/BackendApi';
import { LeaveWithoutSavingModal } from 'ui/LeaveWithoutSavingModal';
import FluidButton from 'ui/FluidButton';
import Checkbox from 'ui/Checkbox';
import { AddMarkupModal } from './AddMarkupModal';
import { EditMarkupModal } from './EditMarkupModal';
import { ProductTypes } from 'config/enums';
import { StackedIcon } from 'ui/StackedIcon';

import {
  addMarkupModalStateSelector,
  chmCloseDeleteModalAction,
  chmDeleteLoadSelector,
  chmMarkupToDeleteSelector,
  chmOpenDeleteModalAction,
  companyHotelMarkupListLoadSelector,
  companyHotelMarkupListSelector,
  companyHotelMarkupSortFieldSelector,
  companyHotelMarkupSortOrderSelector,
  bulkEditSelector,
  bulkDeleteSelector,
  editMarkupRequestAction,
  deleteCHMarkupRequestAction,
  fetchCHMarkupListRequestAction,
  openCreateMarkupModalAction,
  setCHMarkupEditingModeAction,
  setCHMarkupPercentageEditedAction,
  setCHMarkupSortFieldAction,
  chmEditLoadSelector,
  toggleMarkupAction,
  toggleAllMarkupsAction,
  selectedSelector,
  openBulkDeleteModalAction,
  closeBulkDeleteModalAction,
  openBulkEditModalAction,
  fetchProductsWithCompanyHotelMarkupRequestAction,
  productsWithCompanyHotelMarkupSelector,
  productsWithCompanyHotelMarkupLoadSelector,
} from 'store/modules/companyHotelMarkup';
import { formatDateDisplay } from 'utils';
import { CHMarkupSortField, ICompanyMarkupOptionModel } from 'store/modules/companyHotelMarkup/model';
import { CircleIconButton } from 'ui/CircleIconButton';
import { NumericMaskedInput } from 'ui/stateful/MaskedInput';
import ConfirmationModal, { EConfirmationModalType } from 'ui/ConfirmationModal';
import { getCountryName } from 'utils/country';
import { DecimalInput } from 'ui/stateful/DecimalInput';
import { theme } from '../../../../tailwind.config';
import * as CompanyInfoActions from 'store/modules/companyInfo/actions';
import * as CompanyInfoSelectors from 'store/modules/companyInfo/selectors';
import { HidingTextTooltip } from 'ui/Tooltip/HidingTextTooltip';
import { useDynamicParameters } from 'context/DynamicParameters/DynamicParametersContext';

interface ICompanyAdditionalMarkupsProps {
  isEditMode: boolean;
}

function getApplicableMarkupMessage(productTypes: string[]) {
  const productTypeLabelMapping = {
    [ProductTypes.ACCOMMODATION]: 'Accommodation (including Extra person supplement)',
    [ProductTypes.MEAL_PLAN]: 'Meal Plans',
    [ProductTypes.TRANSFER]: 'Transfers',
    [ProductTypes.GROUND_SERVICE]: 'Ground services',
    [ProductTypes.FINE]: 'Fines',
  };

  const productLabels = productTypes.map(item => productTypeLabelMapping[item]);
  if (productLabels.length === 0) {
    return '';
  }
  const commaSeparated = initial(productLabels).join(', ');
  const andSeparated = [commaSeparated, last(productLabels)].filter(item => !!item).join(' and ');

  const message = `The Mark-ups will be applied ON TOP of the selling price for ${andSeparated}.`;

  return message;
}

const markUpToColWidth = '280px';
const typeColWidth = '90px';
const locationColWidth = '160px';
const markupColWidth = '150px';
const createdAtColWidth = '90px';
const actionsColWidth = '110px';

export const AdditionalMarkups: React.FC<ICompanyAdditionalMarkupsProps> = ({ isEditMode }) => {
  const { dynamicParameters } = useDynamicParameters();
  const chMarkupList: ICompanyMarkupOptionModel[] = useSelector(companyHotelMarkupListSelector);
  const chMarkupSelected: ICompanyMarkupOptionModel[] = useSelector(selectedSelector);
  const chMarkupListLoad = useSelector(companyHotelMarkupListLoadSelector);
  const sortField = useSelector(companyHotelMarkupSortFieldSelector);
  const sortOrder = useSelector(companyHotelMarkupSortOrderSelector);

  const bulkDelete = useSelector(bulkDeleteSelector);
  const bulkEdit = useSelector(bulkEditSelector);

  const isAddMarkupModalOpen = useSelector(addMarkupModalStateSelector);
  const isDeleteModalOpen = !!useSelector(chmMarkupToDeleteSelector);
  const isBulkDeleteModalOpen = !!bulkDelete;
  const isBulkEditModalOpen = !!bulkEdit;

  const chmDeleteLoadStatus = useSelector(chmDeleteLoadSelector);
  const isDeleteInProgress = chmDeleteLoadStatus === ENetworkRequestStatus.PENDING;
  const chmEditLoadStatus = useSelector(chmEditLoadSelector);
  const isEditInProgress = chmEditLoadStatus === ENetworkRequestStatus.PENDING;
  const dispatch = useDispatch();
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const productTypesWithCHMarkup: string[] = useSelector(productsWithCompanyHotelMarkupSelector);
  const productTypesWithCHMarkupLoad = useSelector(productsWithCompanyHotelMarkupLoadSelector);

  const companyExternalMarkupPercentage = useSelector(CompanyInfoSelectors.companyExternalMarkupPercentageSelector);
  const customServiceCharge = useSelector(CompanyInfoSelectors.customServiceChargeSelector);
  const handleCustomServiceChargeChange = (value: number | null) => {
    dispatch(CompanyInfoActions.updateCustomServiceChargeAction(value === null ? null : String(value)));
    setHasChanges(true);
  };

  useEffect(() => {
    dispatch(fetchCHMarkupListRequestAction());
    dispatch(fetchProductsWithCompanyHotelMarkupRequestAction());
  }, [dispatch]);

  const handleSort = useCallback(
    (sortBy: CHMarkupSortField) => () => {
      const newSortOrder = sortBy === sortField && sortOrder === 'asc' ? 'desc' : 'asc';
      dispatch(setCHMarkupSortFieldAction(sortBy, newSortOrder));
    },
    [dispatch, sortField, sortOrder]
  );

  const handleAddMarkup = useCallback(() => {
    dispatch(openCreateMarkupModalAction());
  }, [dispatch]);

  const handleDeleteMarkup = useCallback(
    (companyUuid: string, option: ICompanyMarkupOptionModel) => () => {
      dispatch(chmOpenDeleteModalAction(companyUuid, option));
    },
    [dispatch]
  );

  const handleConfirmDeleteMarkup = useCallback(() => {
    dispatch(deleteCHMarkupRequestAction());
  }, [dispatch]);

  const handleDeleteModalClose = useCallback(() => {
    dispatch(chmCloseDeleteModalAction());
  }, [dispatch]);

  const handleBulkDeleteModalClose = useCallback(() => {
    dispatch(closeBulkDeleteModalAction());
  }, [dispatch]);

  const renderHeaderSpan = (headerLabel: ReactNode, headerSortByKey: CHMarkupSortField, sortBy?: CHMarkupSortField) => {
    return (
      <span
        className={classnames(
          'group flex font-normal items-center px-2 justify-between h-8 hover:bg-gray-10 cursor-pointer rounded',
          {
            rounded: sortBy === headerSortByKey,
            'bg-gray-15': sortBy === headerSortByKey,
          }
        )}
      >
        <span>{headerLabel}</span>
        {renderSortIcon(headerSortByKey)}
      </span>
    );
  };

  const handleEditMarkup = useCallback(
    (companyUuid: string, option: ICompanyMarkupOptionModel) => () => {
      dispatch(setCHMarkupEditingModeAction(companyUuid, option, true));
      setHasChanges(true);
    },
    [dispatch]
  );

  const handleConfirmEdit = useCallback(
    (companyUuid: string, option: ICompanyMarkupOptionModel) => () => {
      dispatch(editMarkupRequestAction(companyUuid, option));
      setHasChanges(false);
    },
    [dispatch]
  );

  const handleDiscardEdit = useCallback(
    (companyUuid: string, option: ICompanyMarkupOptionModel) => () => {
      dispatch(setCHMarkupEditingModeAction(companyUuid, option, false));
      setHasChanges(false);
    },
    [dispatch]
  );

  const handleMarkupPercentageChange = useCallback(
    (companyUuid: string, option: ICompanyMarkupOptionModel) => (markupPercentage: string) => {
      dispatch(setCHMarkupPercentageEditedAction(companyUuid, option, markupPercentage));
    },
    [dispatch]
  );

  const handleToggle = useCallback((option: ICompanyMarkupOptionModel) => dispatch(toggleMarkupAction(option)), [
    dispatch,
  ]);

  const handleToggleAll = useCallback(
    (e?) => {
      if (e) {
        e.stopPropagation();
      }
      dispatch(toggleAllMarkupsAction());
    },
    [dispatch]
  );

  const handleDeleteSelected = useCallback(() => dispatch(openBulkDeleteModalAction()), [dispatch]);

  const handleEditSelected = useCallback(() => dispatch(openBulkEditModalAction()), [dispatch]);

  const renderSortIcon = (newSortField: CHMarkupSortField) => {
    const isActive = newSortField === sortField;
    const isAscending = sortOrder === 'asc';

    // When inactive or on hover: both carets are gray-40 (or ivory when not active)
    // When active ascending: up is gray-100, down is gray-40
    // When active descending: up is gray-40, down is gray-100
    const upFillColor = isActive && isAscending ? 'fill-gray-100' : 'fill-gray-40';
    const downFillColor = isActive && !isAscending ? 'fill-gray-100' : 'fill-gray-40';

    return (
      <span
        style={{ marginRight: '-18px', width: '38px' }}
        className={classnames({
          'group-hover:[&_svg]:fill-gray-40': !isActive,
          '[&_svg]:fill-ivory': !isActive,
        })}
      >
        <StackedIcon
          stackDirection="vertical"
          TopIcon={CaretUpIcon}
          topClassName={`w-3 h-3 ${upFillColor}`}
          BottomIcon={CaretDownIcon}
          bottomClassName={`w-3 h-3 ${downFillColor}`}
        />
      </span>
    );
  };

  const renderHotelHeader = () => (
    <label className="flex flex-row items-center">
      <Checkbox
        checked={chMarkupList.length === chMarkupSelected.length}
        onClick={handleToggleAll}
        readOnly
        onChange={() => {
          /* void */
        }}
      />
      <span className="ml-2 uppercase">Mark-Up To</span>
    </label>
  );

  return (
    <div className="additional-markups-section w-full">
      <p className="additional-markups-title font-pt-sans text-black-true font-bold text-17px leading-md mt-25px mb-0">
        External Mark-Up
      </p>

      <div className="mt-5px flex items-center font-pt-sans space-x-8px">
        <DecimalInput
          className="w-[65px] h-[35px] border border-solid border-gray-40 bg-ivory text-center text-15px focus:outline-solid focus:outline-teal-80 focus:outline-1"
          decimalPlaces={2}
          onBlur={val => {
            dispatch(CompanyInfoActions.updateCompanyExternalMarkupPercentageAction(val ? val.toString() : null));
          }}
          allowNull={true}
          value={companyExternalMarkupPercentage ? companyExternalMarkupPercentage : ''}
        />
        <span className="text-13px leading-16px text-black">%</span>
        <span className="text-13px leading-16px text-black w-[91px]">Including two decimals (##.##)</span>
        <span>
          <HidingTextTooltip
            tooltipContent="This will apply ONLY to Accommodations (including Extra Person rate), Meal Plans, Transfers, Ground Services and Fines."
            position="right-bottom"
            width="150px"
            tooltipContentClassname="px-[7px] py-[5px]"
          >
            <InfoIcon className="w-[18px]" fill={theme.colors['gray-80']} />
          </HidingTextTooltip>
        </span>
      </div>

      {dynamicParameters.ENABLE_CRM_WORKSPACE_TEMP && (
        <>
          <p className="additional-markups-title font-pt-sans text-black-true font-bold text-17px leading-md mt-25px mb-0">
            Custom Service Charge
          </p>

          <div className="relative flex flex-col serviceChargeOverride w-200px">
            <DecimalInput
              className={classnames(
                'text-15px leading-19px py-7px px-10px mt-5px font-pt-sans outline-none text-black border border-solid border-gray-40 bg-ivory'
              )}
              value={customServiceCharge === null ? '' : customServiceCharge}
              allowNull
              decimalPlaces={2}
              onBlur={handleCustomServiceChargeChange}
              disabled={!isEditMode}
            />
            <span className="absolute font-pt-sans top-[12px] right-10px text-gray-80 select-none pointer-events-none">
              U$S/€
            </span>
          </div>
        </>
      )}

      <p className="additional-markups-title font-pt-sans text-black-true font-bold text-17px leading-md mt-25px mb-0">
        Additional Mark-Ups (Internal)
      </p>
      {productTypesWithCHMarkupLoad === ENetworkRequestStatus.SUCCESS && (
        <p className="additional-markups-description font-pt-sans text-black text-15px leading-sm mt-5px mb-0">
          {getApplicableMarkupMessage(productTypesWithCHMarkup)}
        </p>
      )}
      <div className="flex items-center justify-between mt-20px">
        <div className="controls flex items-center">
          {isEditMode && (
            <FluidButton
              type="secondary"
              className="delete-selected"
              onClick={handleDeleteSelected}
              disabled={!chMarkupSelected.length}
            >
              Delete Selected <SvgIcon IconComponent={TrashIcon} className="w-4 h-4 fill-transparent stroke-brown-100 stroke-2 ml-2" />
            </FluidButton>
          )}
          {isEditMode && (
            <FluidButton
              type="secondary"
              className="edit-selected ml-10px"
              onClick={handleEditSelected}
              disabled={!chMarkupSelected.length || !isEditMode}
            >
              Edit Selected
            </FluidButton>
          )}
        </div>
        <p className="markups-quantity font-pt-sans text-brown-100 text-13px leading-sm">
          <span className="font-bold">{chMarkupList?.length ?? 0}</span> Mark-Ups Added
        </p>
      </div>

      {(chMarkupListLoad === ENetworkRequestStatus.PENDING ||
        productTypesWithCHMarkupLoad === ENetworkRequestStatus.PENDING) && (
        <div className="mt-20px">
          <LoadingBar />
        </div>
      )}

      {chMarkupListLoad === ENetworkRequestStatus.ERROR && (
        <div className="mt-20px">
          <ErrorBar />
        </div>
      )}

      {chMarkupListLoad === ENetworkRequestStatus.SUCCESS && chMarkupList?.length > 0 && (
        <table className="additional-markups-table table-fixed inline-block border-collapse text-left font-pt-sans mt-15px">
          <thead>
            <tr className="additional-markups-table-headers bg-ivory border border-solid border-gray-40 shadow-pe2 h-10 uppercase text-xs text-gray-120 leading-16px tracking-2xs text-balance">
              <th
                id="hotelName-header"
                style={{ minWidth: markUpToColWidth, maxWidth: markUpToColWidth }}
                className="pl-4"
                onClick={handleSort('name')}
              >
                {renderHeaderSpan(renderHotelHeader(), 'name', sortField)}
              </th>
              <th
                id="location-header"
                style={{ minWidth: typeColWidth, maxWidth: typeColWidth }}
                onClick={handleSort('type')}
              >
                {renderHeaderSpan('type', 'type', sortField)}
              </th>
              <th
                id="location-header"
                style={{ minWidth: locationColWidth, maxWidth: locationColWidth }}
                onClick={handleSort('countryName')}
              >
                {renderHeaderSpan('location', 'countryName', sortField)}
              </th>

              <th
                id="markUp-header"
                style={{ minWidth: markupColWidth, maxWidth: markupColWidth }}
                onClick={handleSort('markupPercentage')}
              >
                {renderHeaderSpan('mark-up (%)', 'markupPercentage', sortField)}
              </th>

              <th
                id="createdAt-header"
                style={{ minWidth: createdAtColWidth, maxWidth: createdAtColWidth }}
                onClick={handleSort('createdAt')}
              >
                {renderHeaderSpan('created', 'createdAt', sortField)}
              </th>

              <th style={{ minWidth: actionsColWidth, maxWidth: actionsColWidth }}>{/* actions */}</th>
            </tr>
          </thead>
          <tbody className="flex">
            <tr>
              <td className="p-0">
                <div style={{ maxHeight: '350px', overflowY: 'auto' }}>
                  <table className="w-full border-collapse" cellSpacing="0">
                    <tbody>
                      {chMarkupList &&
                        chMarkupList.map((item, index) => {
                          const identifier = 'hotelUuid' in item ? item.hotelUuid : item.supplierId.toString();
                          return (
                            <tr
                              key={`${identifier}`}
                              className={classnames('h-50px text-15px leading-19px text-black', {
                                'bg-teal-15': item.isEditMode,
                                'bg-ivory': index % 2 === 1 && !item.isEditMode,
                              })}
                            >
                              <td
                                className="pl-6 text-ellipsis overflow-hidden border-solid border-b border-b-gray-20 text-balance"
                                style={{ minWidth: markUpToColWidth, maxWidth: markUpToColWidth }}
                              >
                                <label
                                  className="flex flex-row items-center cursor-pointer"
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleToggle(item);
                                  }}
                                >
                                  <Checkbox
                                    checked={item.selected}
                                    readOnly
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleToggle(item);
                                    }}
                                    onChange={() => {
                                      /*void */
                                    }}
                                  />
                                  <span className="ml-2">{item.name}</span>
                                </label>
                              </td>
                              <td
                                className="px-2 text-ellipsis overflow-hidden border-solid border-b border-b-gray-20 text-balance"
                                style={{ minWidth: typeColWidth, maxWidth: typeColWidth }}
                              >
                                {item.type}
                              </td>
                              <td
                                className="px-2 text-ellipsis overflow-hidden whitespace-nowrap border-solid border-b border-b-gray-20"
                                style={{ minWidth: locationColWidth, maxWidth: locationColWidth }}
                              >
                                {getCountryName(item.countryCode)}
                              </td>
                              <td
                                className="px-2 border-solid border-b border-b-gray-20"
                                style={{ minWidth: markupColWidth, maxWidth: markupColWidth }}
                              >
                                {item.isEditMode ? (
                                  <div className="edit-markup-input flex items-center">
                                    <div className="w-15 border-3 border border-solid border-teal-80">
                                      <NumericMaskedInput
                                        initialValue={
                                          typeof item.markupPercentage === 'number'
                                            ? item.markupPercentage.toFixed(2)
                                            : item.markupPercentage
                                        }
                                        value={
                                          typeof item.markupPercentage === 'number'
                                            ? item.markupPercentage.toFixed(2)
                                            : item.markupPercentage
                                        }
                                        disabled={isEditInProgress}
                                        mask="##.##"
                                        onChange={handleMarkupPercentageChange(item.companyUuid, item)}
                                        inputClassName="edit-markup-input-element"
                                        allowNegative
                                      />
                                    </div>
                                    <span className="font-pt-sans text-13px leading-16px text-black font-normal ml-5px">
                                      % Two decimals (##.##)
                                    </span>
                                  </div>
                                ) : (
                                  `${
                                    typeof item.markupPercentage === 'number'
                                      ? item.markupPercentage.toFixed(2)
                                      : item.markupPercentage
                                  } %`
                                )}
                              </td>
                              <td
                                className="px-2 border-solid border-b border-b-gray-20"
                                style={{ minWidth: createdAtColWidth, maxWidth: createdAtColWidth }}
                              >
                                {formatDateDisplay(item.createdAt)}
                              </td>
                              <td
                                className="px-1 border-solid border-b border-b-gray-20"
                                style={{ minWidth: actionsColWidth, maxWidth: actionsColWidth }}
                              >
                                <div className="flex items-center ml-2">
                                  <div className="w-1 h-30px bg-brown-15 rounded"></div>
                                  {item.isEditMode ? (
                                    <React.Fragment>
                                      <CircleIconButton
                                        style={{ paddingTop: '2px' }}
                                        className="chm-row-edit-confirm mr-1 ml-3 w-30px h-30px relative"
                                        IconComponent={CheckIcon}
                                        iconSize="default"
                                        disabled={isEditInProgress}
                                        type={'primary'}
                                        onClick={handleConfirmEdit(item.companyUuid, item)}
                                      />
                                      <CircleIconButton
                                        style={{ paddingTop: '2px' }}
                                        className="chm-row-edit-discard mx-1 w-30px h-30px relative"
                                        IconComponent={XIcon}
                                        iconSize="default"
                                        type="secondary"
                                        onClick={handleDiscardEdit(item.companyUuid, item)}
                                      />
                                    </React.Fragment>
                                  ) : (
                                    <React.Fragment>
                                      {isEditMode && (
                                        <CircleIconButton
                                          type="secondary"
                                          className="chm-row-edit-button mr-1 ml-3 w-30px h-30px "
                                          IconComponent={PencilIcon}
                                          iconSize="default"
                                          onClick={handleEditMarkup(item.companyUuid, item)}
                                        />
                                      )}
                                      {isEditMode && (
                                        <CircleIconButton
                                          type="secondary"
                                          className="chm-row-delete-button mx-1 w-30px h-30px"
                                          IconComponent={TrashIcon}
                                          iconSize="default"
                                          onClick={handleDeleteMarkup(item.companyUuid, item)}
                                        />
                                      )}
                                    </React.Fragment>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {isEditMode && (
        <FluidButton
          type="secondary"
          className="add-additional-markup mt-20px"
          onClick={handleAddMarkup}
          disabled={!isEditMode}
        >
          Add Additional Mark-Up
        </FluidButton>
      )}

      <AddMarkupModal isOpen={isAddMarkupModalOpen} />
      <EditMarkupModal isOpen={isBulkEditModalOpen} />

      <ConfirmationModal
        className="additional-markups-delete-warning"
        type={EConfirmationModalType.WARNING}
        isOpen={isDeleteModalOpen}
        title="Delete Additional Mark-Up"
        message="Are you sure you want to delete this mark-up?"
        extraMessage="This cannot be undone!"
        confirmButtonLabel="Delete"
        cancelButtonLabel="Cancel"
        isConfirmLoading={isDeleteInProgress}
        onConfirm={handleConfirmDeleteMarkup}
        onCancel={handleDeleteModalClose}
      />

      <ConfirmationModal
        className="additional-markups-delete-selected-warning"
        type={EConfirmationModalType.WARNING}
        isOpen={isBulkDeleteModalOpen}
        title="Delete Additional Mark-Ups"
        message="Are you sure want to delete the items selected?"
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
        isConfirmLoading={isDeleteInProgress}
        onConfirm={handleConfirmDeleteMarkup}
        onCancel={handleBulkDeleteModalClose}
      />

      <LeaveWithoutSavingModal when={hasChanges} />
    </div>
  );
};
